/**
 * B1.4-E — bounded display, validation and error-presentation helpers for
 * authenticated patient-name capture and display.
 *
 * Pure functions only: nothing here issues a request, holds state or logs. No
 * helper writes a patient-name component, an identifier, a request body, a
 * response body, a provider error or a database error anywhere — the operator
 * messages are fixed strings and are never interpolated with submitted or
 * stored values.
 *
 * Requirements supported at the display layer: HI_005_005804 / HI_010_005804 /
 * HI_015_005804 (explicit given/family roles plus the HI-association
 * indication), HI_010_005809 / HI_015_005809 (other-name capture) and
 * HI_015_005824 (retained previous-name history).
 */

import axios from 'axios';

import {
	PatientNameContext,
	PatientOtherNameSubmission,
	PatientStructuredName,
} from '../../../../types/healthcare/PatientNameTypes';

// ── Explicit role labels ─────────────────────────────────────────────────────
//
// The three canonical role labels. "First"/"Last" are avoided deliberately:
// they describe field order rather than the given/family roles an IHI search is
// built from, and they read wrongly for a one-name-only record.

export const GIVEN_NAME_LABEL = 'Given name';
export const MIDDLE_NAME_LABEL = 'Middle name';
export const FAMILY_NAME_LABEL = 'Family name';
export const ONE_NAME_ONLY_LABEL = 'One name only';

// ── HI-associated-name indication ────────────────────────────────────────────

export const HI_ASSOCIATED_NAME_LABEL = 'HI-associated name';

/**
 * The exact meaning of the indication. It is a bounded LOCAL provenance
 * statement and is deliberately not a claim about current HI Service content.
 */
export const HI_ASSOCIATED_NAME_HELP_TEXT =
	'MyGene records this as the exact name used in an approved verified-IHI interaction. ' +
	'This is not a live statement about the name currently held by the HI Service.';

/** DOM id of the single shared help text every indication is described by. */
export const HI_ASSOCIATED_NAME_HELP_ID = 'patient-hi-associated-name-help';

// ── Fixed operator messages ──────────────────────────────────────────────────

/**
 * One generic unavailable boundary. The server answers an inaccessible patient,
 * an unknown patient, an unknown or cross-patient name id, a previous row and an
 * already-superseded row with the same bounded not-found response, so the
 * operator-facing text must not distinguish them either.
 */
export const PATIENT_NAME_UNAVAILABLE_MESSAGE =
	'That patient name record is not available.';

export const PATIENT_NAME_DUPLICATE_MESSAGE =
	'An identical active other name is already recorded for this patient.';

export const PATIENT_NAME_INVALID_MESSAGE =
	'The name details were not accepted. Check the given, middle and family name fields and the one-name-only setting.';

export const PATIENT_NAME_UNEXPECTED_MESSAGE =
	'The patient name operation could not be completed. Please try again.';

export const PATIENT_NAMES_LOAD_FAILED_MESSAGE =
	'Patient names could not be loaded.';

export const PATIENT_OTHER_NAME_CREATED_MESSAGE = 'Other name recorded.';
export const PATIENT_OTHER_NAME_REPLACED_MESSAGE =
	'Other name updated. The previous entry has been retained in the name history.';
export const PATIENT_OTHER_NAME_SUPERSEDED_MESSAGE =
	'Other name superseded. It has been retained in the name history.';

/** Fixed validation messages for locally captured name components. */
export const FAMILY_NAME_REQUIRED_MESSAGE = 'A family name is required.';
export const GIVEN_NAME_REQUIRED_MESSAGE =
	'A given name is required unless the name is recorded as one name only.';

/**
 * Map a failed patient-name request to its fixed operator message.
 *
 * Only the HTTP status category is inspected. No response body, backend message,
 * validation detail, SQL, provider error or exception text is read or shown: an
 * unrecognised failure takes the fixed unexpected-operation path.
 */
export const mapPatientNameError = (error: unknown): string => {
	const status = axios.isAxiosError(error) ? error.response?.status : undefined;

	switch (status) {
		// 403 and 404 share the one generic unavailable boundary: an inaccessible
		// record must not be distinguishable from an unknown one.
		case 403:
		case 404:
			return PATIENT_NAME_UNAVAILABLE_MESSAGE;
		case 409:
			return PATIENT_NAME_DUPLICATE_MESSAGE;
		case 422:
			return PATIENT_NAME_INVALID_MESSAGE;
		default:
			return PATIENT_NAME_UNEXPECTED_MESSAGE;
	}
};

// ── Name component display ───────────────────────────────────────────────────

interface NameComponents {
	family_name?: string | null;
	given_name?: string | null;
	middle_name?: string | null;
	is_one_name_only?: boolean | null;
}

const trimmed = (value?: string | null): string => (value ?? '').trim();

/**
 * The explicit role breakdown of one name, omitting every component that is not
 * recorded.
 *
 * A one-name-only record yields exactly one part in the FAMILY-name role — it
 * never reports an empty given name and never repeats the single name under a
 * second role. An ordinary record with no middle name yields two parts rather
 * than a blank middle-name slot.
 */
export const patientNameRoleParts = (
	name: NameComponents
): Array<{ label: string; value: string }> => {
	const family = trimmed(name.family_name);

	if (name.is_one_name_only === true) {
		return family ? [{ label: FAMILY_NAME_LABEL, value: family }] : [];
	}

	const parts: Array<{ label: string; value: string }> = [];
	const given = trimmed(name.given_name);
	const middle = trimmed(name.middle_name);

	if (given) parts.push({ label: GIVEN_NAME_LABEL, value: given });
	if (middle) parts.push({ label: MIDDLE_NAME_LABEL, value: middle });
	if (family) parts.push({ label: FAMILY_NAME_LABEL, value: family });

	return parts;
};

/**
 * Readable single-line form of one name, in given → middle → family order, with
 * unrecorded components dropped so no misleading blank or double space appears.
 * A one-name-only record renders as the single name alone.
 */
export const formatPatientNameDisplay = (name: NameComponents): string =>
	patientNameRoleParts(name)
		.map((part) => part.value)
		.join(' ');

/**
 * Readable single-line form of a patient's canonical primary name, honouring the
 * explicit one-name-only state.
 */
export const formatPatientPrimaryDisplayName = (patient: {
	first_name?: string | null;
	middle_name?: string | null;
	last_name?: string | null;
	has_one_name_only?: boolean;
}): string =>
	formatPatientNameDisplay({
		given_name: patient.first_name,
		middle_name: patient.middle_name,
		family_name: patient.last_name,
		is_one_name_only: patient.has_one_name_only === true,
	});

/**
 * Derived identifier-safe slug of the canonical primary name, used only for the
 * generated username preview and value. Unrecorded components are dropped, so a
 * one-name-only record does not produce a leading separator. An ordinary record
 * with both components is unchanged.
 */
export const patientPrimaryNameSlug = (patient: {
	first_name?: string | null;
	last_name?: string | null;
}): string => [trimmed(patient.first_name), trimmed(patient.last_name)].filter(Boolean).join('_');

/**
 * The username a not-yet-created patient record will be created with.
 *
 * P2 (86d3yf9q7): the bare `first_last` slug, never auto-suffixed — or the
 * operator's own value once they have edited it to resolve a duplicate-name
 * collision. ProfileTab PREVIEWS this and PatientModal SUBMITS it, from this
 * one helper, so the previewed username can no longer promise something the
 * create does not use. (It previously previewed `${slug}_${timestamp}` while
 * the bare slug was submitted, which made the unique-username 422 look like it
 * came out of nowhere.)
 */
export const patientCreateUsername = (patient: {
	username?: string | null;
	first_name?: string | null;
	last_name?: string | null;
}): string => trimmed(patient.username) || patientPrimaryNameSlug(patient);

/**
 * The operator-facing message for a username already in use.
 *
 * P2 (86d3yf9q7): keeping the bare slug means two patients genuinely recorded
 * under the same name collide on `unique:users,username`. That is a real
 * workflow, so it gets a message that says what happened and what to do, rather
 * than the server's raw field error naming a request path.
 */
export const PATIENT_USERNAME_TAKEN_MESSAGE =
	'A patient with this name already exists. Adjust the username before saving.';

/**
 * Is this one field of a 422 validation body the username-already-taken
 * rejection?
 *
 * Only the field KEY and the rule the message describes are inspected, and
 * nothing from the response is carried into the operator message. A username
 * field error for any OTHER rule (a length failure, say) is left alone so it is
 * not mis-reported as a collision.
 */
export const isDuplicateUsernameFieldError = (field: string, messages: string[]): boolean =>
	field.split('.').pop() === 'username' &&
	messages.some((message) => /already been taken|already exists|unique/i.test(message));

// ── Lifecycle and context labels ─────────────────────────────────────────────

/**
 * Bounded lifecycle label for a history row.
 *
 * The two retained shapes stay distinct in both directions and neither is ever
 * converted into the other:
 *
 *  - `previous` + `primary` — a primary name replaced by a newer one;
 *  - `previous` + `other`   — an other name retained as previous;
 *  - `other` + superseded   — an other name retired from the active set.
 *
 * Any other bounded combination is presented conservatively as generic read-only
 * name history rather than being reinterpreted.
 */
export const patientNameHistoryLabel = (name: PatientStructuredName): string => {
	if (name.name_type === 'previous') {
		if (name.previous_role === 'primary') return 'Previous primary name';
		if (name.previous_role === 'other') return 'Previous other name';
		return 'Name history';
	}

	if (name.name_type === 'other' && name.superseded_at) return 'Superseded other name';

	return 'Name history';
};

const CONTEXT_LABELS: Record<PatientNameContext, string> = {
	general: 'General',
	medicare_card: 'Medicare card',
	preferred: 'Preferred',
};

/**
 * Bounded context label. Context is a separate axis from lifecycle/type and is
 * never rendered as one. An unrecognised value is reported as unspecified rather
 * than guessed at.
 */
export const patientNameContextLabel = (context?: PatientNameContext | null): string =>
	(context && CONTEXT_LABELS[context]) || 'Not specified';

// ── Local validation ─────────────────────────────────────────────────────────

/**
 * Local component rules for one other-name submission, mirroring the server's
 * bounded contract without weakening it — the authentication service remains the
 * authority and re-applies every rule.
 */
export const validatePatientOtherName = (
	submission: PatientOtherNameSubmission
): string[] => {
	const errors: string[] = [];

	if (!trimmed(submission.family_name)) errors.push(FAMILY_NAME_REQUIRED_MESSAGE);

	if (submission.is_one_name_only !== true && !trimmed(submission.given_name)) {
		errors.push(GIVEN_NAME_REQUIRED_MESSAGE);
	}

	return errors;
};

/**
 * Normalise the canonical primary-name components of an outgoing save payload.
 *
 * This is the FINAL save boundary, applied independently of whatever the form
 * state happens to hold. A one-name-only record has no given or middle
 * component at all, so both are forced to empty strings here even when stale
 * values survived in component state — the single recorded name stays in the
 * family-name role, which is the only place the form captures it.
 *
 * A fresh object is always returned and the input is never mutated, so a save
 * can neither alias nor write back into the caller's patient state.
 *
 * An ordinary record is copied through untouched: coercing an absent middle name
 * to an empty string here would submit a blanking value for a field the operator
 * never edited.
 */
export const normalisePatientPrimaryNamePayload = <
	T extends {
		first_name?: string | null;
		middle_name?: string | null;
		last_name?: string | null;
		has_one_name_only?: boolean;
	}
>(
	patient: T
): T => {
	if (patient.has_one_name_only !== true) return { ...patient };

	return { ...patient, has_one_name_only: true, first_name: '', middle_name: '' };
};

/**
 * Local component rules for the canonical primary name.
 *
 * A one-name-only record requires only the family-name role. An ordinary record
 * requires both the given and the family name; the middle name stays optional.
 */
export const validatePatientPrimaryName = (patient: {
	first_name?: string | null;
	last_name?: string | null;
	has_one_name_only?: boolean;
}): string[] => {
	const errors: string[] = [];

	if (!trimmed(patient.last_name)) errors.push(FAMILY_NAME_REQUIRED_MESSAGE);

	if (patient.has_one_name_only !== true && !trimmed(patient.first_name)) {
		errors.push(GIVEN_NAME_REQUIRED_MESSAGE);
	}

	return errors;
};

// ── Primary-name edit-session draft ──────────────────────────────────────────

/**
 * The four values that make up one explicitly edited canonical primary name.
 *
 * CU-86d439zzf: an existing patient's primary name is edited as one group and
 * becomes authoritative only through an explicit save, so a half-edited tuple
 * can never reach the server and be retained as previous-name history.
 */
export interface PatientPrimaryNameDraft {
	first_name: string;
	middle_name: string;
	last_name: string;
	has_one_name_only: boolean;
}

/** The committed primary-name tuple of a patient record, as an edit draft. */
export const patientPrimaryNameDraftFromPatient = (patient: {
	first_name?: string | null;
	middle_name?: string | null;
	last_name?: string | null;
	has_one_name_only?: boolean;
}): PatientPrimaryNameDraft => ({
	first_name: patient.first_name ?? '',
	middle_name: patient.middle_name ?? '',
	last_name: patient.last_name ?? '',
	has_one_name_only: patient.has_one_name_only === true,
});

/** Exact four-value comparison; no trimming, casing or other normalisation. */
export const patientPrimaryNameDraftsMatch = (
	a: PatientPrimaryNameDraft,
	b: PatientPrimaryNameDraft
): boolean =>
	a.first_name === b.first_name &&
	a.middle_name === b.middle_name &&
	a.last_name === b.last_name &&
	a.has_one_name_only === b.has_one_name_only;

/**
 * One-name-only toggled inside an unsaved edit session.
 *
 * CU-86d439zzf: turning it OFF restores the given and middle components
 * captured when the session started, so temporary experimentation with the
 * toggle cannot silently destroy them.
 */
export const applyOneNameOnlyToPrimaryNameDraft = (
	draft: PatientPrimaryNameDraft,
	checked: boolean,
	sessionOriginal: PatientPrimaryNameDraft
): PatientPrimaryNameDraft =>
	checked
		? { ...draft, has_one_name_only: true, first_name: '', middle_name: '' }
		: {
			...draft,
			has_one_name_only: false,
			first_name: sessionOriginal.first_name,
			middle_name: sessionOriginal.middle_name,
		};

/**
 * A fresh patient object carrying the draft tuple. The input is never mutated,
 * so an explicit commit cannot write the draft back into committed form state
 * before the request succeeds.
 */
export const mergePatientPrimaryNameDraft = <
	T extends {
		first_name?: string | null;
		middle_name?: string | null;
		last_name?: string | null;
		has_one_name_only?: boolean;
	}
>(
	patient: T,
	draft: PatientPrimaryNameDraft | null
): T => (draft ? { ...patient, ...draft } : { ...patient });

/**
 * Scoped styling hook for a locked or one-name-only-suppressed NAME control.
 * Applied only to the approved name controls so no other disabled field in the
 * application is restyled.
 */
export const LOCKED_NAME_INPUT_CLASS = 'mg-name-locked';
